import { useEffect, useMemo, useState } from "react";
import { getRoleManagement } from "../services/FetchAPI";

const mapRoleToSelectOptions = (roleList) =>
  roleList.map((role) => ({
    value: role.role_name,
    label: role.role_name,
  }));

const buildRoleMap = (roleList) => {
  const map = {};
  roleList.forEach((role) => {
    map[role.role_id] = role.role_name;
  });
  return map;
};

const buildRoleNameToIdMap = (roleList) => {
  const map = {};
  roleList.forEach((role) => {
    map[role.role_name] = role.role_id;
  });
  return map;
};

export default function useRoleManagement(passtoken) {
  const [roleList, setRoleList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!passtoken) {
      setLoading(false);
      return;
    }

    let isMounted = true;

    const fetchRoles = async () => {
      setLoading(true);
      try {
        const data = await getRoleManagement(passtoken);
        if (isMounted) {
          setRoleList(Array.isArray(data) ? data : []);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          setError(err);
          setRoleList([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchRoles();

    return () => {
      isMounted = false;
    };
  }, [passtoken]);

  const roleOptions = useMemo(
    () => mapRoleToSelectOptions(roleList),
    [roleList]
  );

  const roleMap = useMemo(() => buildRoleMap(roleList), [roleList]);

  const roleNameToIdMap = useMemo(
    () => buildRoleNameToIdMap(roleList),
    [roleList]
  );

  const getRoleLabel = (roleId) => roleMap[roleId] ?? String(roleId);

  const resolveRoleId = (roleItem) => {
    if (!roleItem) return null;
    if (roleItem.roleId != null) return roleItem.roleId;
    if (roleItem.role_id != null) return roleItem.role_id;
    if (roleItem.RLtypename && roleNameToIdMap[roleItem.RLtypename] != null) {
      return roleNameToIdMap[roleItem.RLtypename];
    }
    return null;
  };

  const getRoleDisplayLabel = (roleItem) => {
    const roleId = resolveRoleId(roleItem);
    if (roleId != null && roleMap[roleId]) {
      return roleMap[roleId];
    }
    return roleItem?.RLtypename ?? roleItem?.role_name ?? "";
  };

  return {
    roleList,
    roleOptions,
    roleMap,
    getRoleLabel,
    resolveRoleId,
    getRoleDisplayLabel,
    loading,
    error,
  };
}
